import React, { createContext, useContext, useState, useEffect } from 'react';

const OperacionesCacheContext = createContext();

export const useOperacionesCache = () => {
  const context = useContext(OperacionesCacheContext);
  if (!context) {
    throw new Error('useOperacionesCache must be used within an OperacionesCacheProvider');
  }
  return context;
};

export const OperacionesCacheProvider = ({ children }) => {
  // Estados separados para cada endpoint
  const [operacionesCache, setOperacionesCache] = useState({
    iniciativas: null,
    lastFetched: null,
    isLoading: false,
    error: null
  });
  
  const [desarrolloNegocioCache, setDesarrolloNegocioCache] = useState({
    iniciativas: null,
    lastFetched: null,
    isLoading: false,
    error: null
  });

  // Cargar datos del caché al montar el componente
  useEffect(() => {
    // Cargar caché de operaciones
    const cachedOperaciones = sessionStorage.getItem('operaciones_cache_v2');
    if (cachedOperaciones) {
      try {
        const parsed = JSON.parse(cachedOperaciones);
        const thirtyMinutes = 30 * 60 * 1000;
        const isExpired = parsed.lastFetched && 
          (Date.now() - parsed.lastFetched) > thirtyMinutes;
        
        if (!isExpired && parsed.iniciativas) {
          setOperacionesCache(parsed);
        } else {
          sessionStorage.removeItem('operaciones_cache_v2');
        }
      } catch (error) {
        console.error('Error parsing operaciones cached data:', error);
        sessionStorage.removeItem('operaciones_cache_v2');
      }
    }
    
    // Cargar caché de desarrollo de negocio
    const cachedDesarrolloNegocio = sessionStorage.getItem('desarrollo_negocio_cache_v2');
    if (cachedDesarrolloNegocio) {
      try {
        const parsed = JSON.parse(cachedDesarrolloNegocio);
        const thirtyMinutes = 30 * 60 * 1000;
        const isExpired = parsed.lastFetched && 
          (Date.now() - parsed.lastFetched) > thirtyMinutes;
        
        if (!isExpired && parsed.iniciativas) {
          setDesarrolloNegocioCache(parsed);
        } else {
          sessionStorage.removeItem('desarrollo_negocio_cache_v2');
        }
      } catch (error) {
        console.error('Error parsing desarrollo_negocio cached data:', error);
        sessionStorage.removeItem('desarrollo_negocio_cache_v2');
      }
    }
  }, []);

  // Función helper para obtener el estado y setter correcto según endpoint
  const getCacheState = (endpoint) => {
    if (endpoint === 'desarrollo_negocio') {
      return { cache: desarrolloNegocioCache, setCache: setDesarrolloNegocioCache, key: 'desarrollo_negocio_cache_v2' };
    }
    return { cache: operacionesCache, setCache: setOperacionesCache, key: 'operaciones_cache_v2' };
  };

  // Guardar datos en el caché
  const updateCache = (iniciativas, error = null, endpoint = 'operaciones') => {
    const { setCache, key } = getCacheState(endpoint);
    
    const newData = {
      iniciativas,
      lastFetched: Date.now(),
      isLoading: false,
      error
    };
    
    setCache(newData);
    
    // Guardar en sessionStorage solo si no hay error
    if (!error && iniciativas) {
      try {
        sessionStorage.setItem(key, JSON.stringify(newData));
      } catch (err) {
        console.error(`Error saving to sessionStorage (${key}):`, err);
      }
    }
  };

  // Marcar como cargando
  const setLoading = (loading, endpoint = 'operaciones') => {
    const { setCache } = getCacheState(endpoint);
    setCache(prev => ({ ...prev, isLoading: loading }));
  };

  // Limpiar caché
  const clearCache = (endpoint = 'operaciones') => {
    const { setCache, key } = getCacheState(endpoint);
    setCache({
      iniciativas: null,
      lastFetched: null,
      isLoading: false,
      error: null
    });
    sessionStorage.removeItem(key);
  };

  // Verificar si hay datos válidos en caché
  const hasValidCache = (endpoint = 'operaciones') => {
    const { cache } = getCacheState(endpoint);
    return cache.iniciativas && 
           cache.lastFetched && 
           !cache.error &&
           Array.isArray(cache.iniciativas);
  };

  // Verificar si el caché está expirado (30 minutos)
  const isCacheExpired = (endpoint = 'operaciones') => {
    const { cache } = getCacheState(endpoint);
    if (!cache.lastFetched) return true;
    const thirtyMinutes = 30 * 60 * 1000;
    return (Date.now() - cache.lastFetched) > thirtyMinutes;
  };
  
  // Obtener los datos de caché según endpoint
  const getCacheData = (endpoint = 'operaciones') => {
    const { cache } = getCacheState(endpoint);
    return cache;
  };

  const value = {
    cacheData: operacionesCache, // Por compatibilidad con código existente
    getCacheData,
    updateCache,
    setLoading,
    clearCache,
    hasValidCache,
    isCacheExpired,
  };

  return (
    <OperacionesCacheContext.Provider value={value}>
      {children}
    </OperacionesCacheContext.Provider>
  );
};